import type { GetWebcastStreamQuery, GetWebcastViewerQuery } from '../../../../../generated/graphql-viewer';
import type { LayoutType } from '../layout-changer';
import type { CSSProperties } from 'react';

import { classNames } from '@movingimage-evp/mi-ui-component-library';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { findEventTitle } from 'src/utils/graphql-helpers';

import notFoundImage from '../../../../../assets/not-found.svg';
import { ErrorPage } from '../../../../../components/error-page';
import { Content } from '../../../../components/content';
import { type SlideCuepoint, SlidePreview } from '../../../../components/slide-preview';
import { useSelectedLanguage } from '../../../../providers/selected-language-provider';
import styles from '../../stream.module.css';
import { CoverVideoPlayer } from '../cover-video-player';
import { DraggableArea } from '../draggable-area';
import { ViewerHeader } from '../header/header';

type Props = {
  webcast: GetWebcastStreamQuery['webcast'] & GetWebcastViewerQuery['webcast'];
};

export function VideoOnDemandView({ webcast }: Props) {
  const { t } = useTranslation();
  const { selectedLanguage, primaryLanguage, isCustomLanguage } = useSelectedLanguage();
  const videoWrapperRef = useRef<HTMLDivElement>(null);

  const [slideCuepoint, setSlideCuepoint] = useState<SlideCuepoint | undefined>();
  const [layoutType, setLayoutType] = useState<LayoutType>('sideBySide');
  const [draggableStyle, setDraggableStyle] = useState<CSSProperties>();
  const [layoutSwapped, setLayoutSwapped] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [playerOverlayHidden, setPlayerOverlayHidden] = useState(false);

  const vodSlides = webcast?.pushedSlides;

  const handleOnTimeUpdate = useCallback(
    (currentTime: number) => {
      if (!vodSlides) return;

      const currentTimeInMs = currentTime * 1000;
      const cuepointsInTimeRange = vodSlides?.filter(({ offsetInMs }) => offsetInMs && offsetInMs <= currentTimeInMs);
      const cuepoint = cuepointsInTimeRange?.[cuepointsInTimeRange.length - 1];

      if (cuepoint) {
        setSlideCuepoint((currentSlideCuepoint) => {
          if (currentSlideCuepoint?.index === cuepoint.index) return currentSlideCuepoint;

          return {
            webcastId: webcast?.id,
            groupId: cuepoint.groupId,
            index: cuepoint.index,
          };
        });
      } else {
        setSlideCuepoint((currentSlideCuepoint) => {
          if (currentSlideCuepoint?.index !== undefined) return undefined;
        });
      }
    },
    [vodSlides, webcast?.id]
  );

  useEffect(() => {
    document.addEventListener('fullscreenchange', () => setFullscreen(Boolean(document.fullscreenElement)));
  }, []);

  if (!webcast) return null;

  const { id, accountId, lsproId, streaming, contents, appearance } = webcast;

  const language = isCustomLanguage(selectedLanguage?.code) ? primaryLanguage?.code : selectedLanguage?.code;
  const vodStreams = streaming?.vod?.streams;
  const vodStream = vodStreams?.find((stream) => stream.metadata.language === selectedLanguage?.code);
  const vodVideo = vodStream?.video;
  const offsetInSeconds = vodStream?.offsetInSeconds;
  const title = findEventTitle(contents, language, webcast?.primaryLang.code);
  const primaryColor = appearance?.primaryColor;
  const iconColor = appearance?.iconColor;
  const logoUrl = appearance?.logoUrl || undefined;
  const horizontalLogoUrl = appearance?.horizontalLogoUrl || undefined;
  const slideVisible = slideCuepoint !== undefined;
  const inIframe = window.location !== window.parent.location;

  if (!vodVideo) {
    return (
      <ErrorPage
        data-testid="no-language"
        title={t('viewer.noLanguage.title')}
        subTitle={t('viewer.noLanguage.subTitle')}
        image={notFoundImage}
      />
    );
  }

  return (
    <div
      data-testid="video-on-demand-view"
      className={classNames(styles.wrapper, slideCuepoint && styles.slideVisible, inIframe && styles.inIframe)}
    >
      <main
        id="viewer-main"
        data-testid="viewer-main"
        className={classNames(styles.main, fullscreen && styles.fullscreen)}
      >
        <ViewerHeader
          fullscreen={fullscreen}
          fullScreenButtonVisible={slideVisible}
          layoutType={layoutType}
          onLayoutSwapped={() => setLayoutSwapped(!layoutSwapped)}
          onSetLayoutType={setLayoutType}
          slideVisible={slideVisible}
          logoUrl={horizontalLogoUrl || logoUrl}
          languageSwitchHidden={playerOverlayHidden}
          inIframe={inIframe}
        />

        <section className="lspro-scrollbars">
          <div
            ref={videoWrapperRef}
            data-testid="video-wrapper"
            className={classNames(
              styles.videoWrapper,
              slideVisible && layoutSwapped && styles.layoutSwapped,
              slideVisible && styles[layoutType]
            )}
          >
            <DraggableArea
              data-testid="player-draggable-area"
              enabled={layoutType === 'pictureInPicture' && !layoutSwapped}
              containerRef={videoWrapperRef}
              className={styles.draggable}
              style={draggableStyle}
              onStop={setDraggableStyle}
            >
              <CoverVideoPlayer
                state="On Demand"
                className={styles.player}
                webcastId={id}
                channelId={vodVideo.channelId}
                videoId={vodVideo.videoId}
                accountId={accountId}
                lsproId={lsproId}
                title={title}
                logoUrl={logoUrl || horizontalLogoUrl}
                primaryColor={primaryColor}
                iconColor={iconColor}
                stepButtonsAreVisible
                offsetInSeconds={offsetInSeconds}
                onPlayerTimeUpdate={handleOnTimeUpdate}
                onOverlayVisibilityChange={setPlayerOverlayHidden}
              />
            </DraggableArea>

            {slideCuepoint && (
              <DraggableArea
                data-testid="slide-draggable-area"
                enabled={layoutType === 'pictureInPicture' && layoutSwapped}
                className={styles.draggable}
                style={draggableStyle}
                containerRef={videoWrapperRef}
                onStop={setDraggableStyle}
              >
                <SlidePreview className={styles.slidePreview} slideCuepoint={slideCuepoint} />
              </DraggableArea>
            )}
          </div>

          {!inIframe && <Content className={styles.content} webcast={webcast} language={language} />}
        </section>
      </main>
    </div>
  );
}
